import React, { useRef, useEffect, useState } from "react";
import { useTournament } from "../contexts/TournamentContext";
import { RoundSection } from "./bracket/RoundSection";

interface TournamentBracketProps {
  shouldAnimateScroll?: boolean;
  scrollSpeed?: 'slow' | 'fast';
}

export const TournamentBracket: React.FC<TournamentBracketProps> = ({
  shouldAnimateScroll = false,
  scrollSpeed = 'fast',
}) => {
  // Get tournament data from context
  const { tournament } = useTournament();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize scroll position to bottom on mount
  useEffect(() => {
    if (scrollContainerRef.current && !isInitialized) {
      const container = scrollContainerRef.current;
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
        setIsInitialized(true);
      });
    }
  }, [isInitialized]);

  // Track animation state
  const animationRef = useRef<{
    startTime: number;
    startScroll: number;
    currentScroll: number;
    animationId?: number;
  } | null>(null);

  // Handle scroll animation
  useEffect(() => {
    if (shouldAnimateScroll && scrollContainerRef.current && isInitialized) {
      const container = scrollContainerRef.current;
      const endScroll = 0;
      
      // Cancel any existing animation first
      if (animationRef.current?.animationId) {
        cancelAnimationFrame(animationRef.current.animationId);
      }
      
      // If animation is already running and we're just changing speed, adjust timing
      if (animationRef.current && scrollSpeed === 'fast') {
        // Speed change - maintain current position but adjust timing
        const currentPos = container.scrollTop;
        animationRef.current = {
          startTime: Date.now(),
          startScroll: currentPos,
          currentScroll: currentPos,
        };
      } else if (!animationRef.current) {
        // First time starting - begin from bottom
        container.scrollTop = container.scrollHeight;
        animationRef.current = {
          startTime: Date.now(),
          startScroll: container.scrollHeight - container.clientHeight,
          currentScroll: container.scrollHeight - container.clientHeight,
        };
      }
      
      const { startTime, startScroll } = animationRef.current;
      const duration = scrollSpeed === 'slow' ? 30000 : 3000; // 30s for slow, 3s for fast

      const animateScroll = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Use linear easing for slow, easeInOutCubic for fast
        const easeProgress = scrollSpeed === 'slow' 
          ? progress
          : progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        const currentScroll =
          startScroll - (startScroll - endScroll) * easeProgress;
        container.scrollTop = currentScroll;
        
        if (animationRef.current) {
          animationRef.current.currentScroll = currentScroll;
        }

        if (progress < 1 && animationRef.current) {
          animationRef.current.animationId = requestAnimationFrame(animateScroll);
        } else {
          animationRef.current = null; // Animation complete
        }
      };

      // Start animation immediately
      if (animationRef.current) {
        animationRef.current.animationId = requestAnimationFrame(animateScroll);
      }
    }
  }, [shouldAnimateScroll, scrollSpeed, isInitialized]);

  return (
    <div
      ref={scrollContainerRef}
      className="relative h-full w-full overflow-y-auto overflow-x-auto bg-black"
      style={{ opacity: isInitialized ? 1 : 0, transition: "opacity 0.2s" }}
    >
      {/* Background image - responsive height */}
      <div
        className="absolute inset-x-0 top-0 z-0 min-h-screen"
        style={{
          backgroundImage: "url(/bracketbg3.png)",
          backgroundSize: "cover",
          backgroundPosition: "top center",
          backgroundRepeat: "no-repeat",
          filter: "brightness(0.3)",
          height: "max(200vh, 100%)",
        }}
      />

      {/* Gradient fade to black at bottom - responsive */}
      <div
        className="absolute inset-x-0 top-0 z-0 pointer-events-none min-h-screen"
        style={{
          height: "max(200vh, 100%)",
          background:
            "linear-gradient(to bottom, transparent 0%, transparent 60%, black 100%)",
        }}
      />

      {/* Tournament bracket content - responsive spacing */}
      <div className="relative z-10 min-h-full px-2 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-8 sm:space-y-10 lg:space-y-12">
        {/* Render all rounds using the unified component */}
        {tournament.rounds.map((round, index) => (
          <RoundSection
            key={round.id}
            round={round}
            showDivider={index < tournament.rounds.length - 1}
          />
        ))}
      </div>
    </div>
  );
};
